import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  alpha,
  Chip,
  Fade,
  Card,
  CardContent,
  Grid,
  TablePagination,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MenuBook as MenuBookIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

const ProductionLineTemplates = () => {
  const { companyId } = useParams();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Template detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTemplateDetail, setSelectedTemplateDetail] = useState(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Create/Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [validationError, setValidationError] = useState('');

  // Data states
  const [products, setProducts] = useState([]);
  const [caps, setCaps] = useState([]);
  const [bottles, setBottles] = useState([]);
  const [labels, setLabels] = useState([]);
  const [supplies, setSupplies] = useState([]);
  

  // Product selection states
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [dialogSelectedProduct, setDialogSelectedProduct] = useState(null);
  const [dialogSelectedVariant, setDialogSelectedVariant] = useState(null);
  const [dialogProductVariants, setDialogProductVariants] = useState([]);

  // Component selection states
  const [selectedCaps, setSelectedCaps] = useState([]);
  const [selectedBottles, setSelectedBottles] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedSupplies, setSelectedSupplies] = useState([]);

  // Component selection dialog states
  const [capSelectionDialogOpen, setCapSelectionDialogOpen] = useState(false);
  const [capSearchTerm, setCapSearchTerm] = useState('');
  const [dialogSelectedCap, setDialogSelectedCap] = useState(undefined);

  const [bottleSelectionDialogOpen, setBottleSelectionDialogOpen] = useState(false);
  const [bottleSearchTerm, setBottleSearchTerm] = useState('');
  const [dialogSelectedBottle, setDialogSelectedBottle] = useState(undefined);

  const [labelSelectionDialogOpen, setLabelSelectionDialogOpen] = useState(false);
  const [labelSearchTerm, setLabelSearchTerm] = useState('');
  const [dialogSelectedLabel, setDialogSelectedLabel] = useState(undefined);

  const [supplySelectionDialogOpen, setSupplySelectionDialogOpen] = useState(false);
  const [supplySearchTerm, setSupplySearchTerm] = useState('');
  const [dialogSelectedSupply, setDialogSelectedSupply] = useState(undefined);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTemplates(),
        fetchProducts(),
        fetchCaps(),
        fetchBottles(),
        fetchLabels(),
        fetchSupplies(),
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('production_line_templates')
        .select(`
          *,
          product:product_id(id, name, sku)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setTemplates(data || []);
      setFilteredTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      throw err;
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchCaps = async () => {
    try {
      const { data, error } = await supabase
        .from('caps')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setCaps(data || []);
    } catch (err) {
      console.error('Error fetching caps:', err);
    }
  };

  const fetchBottles = async () => {
    try {
      const { data, error } = await supabase
        .from('bottles')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setBottles(data || []);
    } catch (err) {
      console.error('Error fetching bottles:', err);
    }
  };

  const fetchLabels = async () => {
    try {
      const { data, error } = await supabase
        .from('labels')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setLabels(data || []);
    } catch (err) {
      console.error('Error fetching labels:', err);
    }
  };

  const fetchSupplies = async () => {
    try {
      const { data, error } = await supabase
        .from('supplies')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setSupplies(data || []);
    } catch (err) {
      console.error('Error fetching supplies:', err);
    }
  };


  const applyFilters = () => {
    let result = [...templates];
    
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(template => 
        (template.template_name && template.template_name.toLowerCase().includes(lowercasedSearch)) ||
        (template.product?.name && template.product.name.toLowerCase().includes(lowercasedSearch)) ||
        (template.description && template.description.toLowerCase().includes(lowercasedSearch))
      );
    }
    
    setFilteredTemplates(result);
    setPage(0);
  };

  useEffect(() => {
    fetchInitialData();
  }, [companyId]);

  useEffect(() => {
    if (templates.length > 0) {
      applyFilters();
    }
  }, [templates, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (event, template) => {
    setSelectedTemplate(template);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenDeleteDialog = () => {
    if (!hasPermission('production.line.templates.delete', 'delete')) {
      setError('You do not have permission to delete templates.');
      return;
    }
    
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteTemplate = async () => {
    if (!hasPermission('production.line.templates.delete', 'delete')) {
      setError('You do not have permission to delete templates.');
      return;
    }
    
    if (!selectedTemplate) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('production_line_templates')
        .delete()
        .eq('id', selectedTemplate.id);
      
      if (error) throw error;
      
      await fetchTemplates();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(`Failed to delete template: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateNew = () => {
    if (!hasPermission('production.line.templates.edit', 'write')) {
      setError('You do not have permission to create templates.');
      return;
    }
    
    setEditData({
      template_name: '',
      description: '',
      product_id: '',
      product_variant_id: null,
      product_variant_name: '',
      is_active: true,
      is_default: false
    });
    setSelectedCaps([]);
    setSelectedBottles([]);
    setSelectedLabels([]);
    setSelectedSupplies([]);
    setValidationError('');
    setEditDialogOpen(true);
  };

  const handleEditTemplate = () => {
    if (!hasPermission('production.line.templates.edit', 'write')) {
      setError('You do not have permission to edit templates.');
      return;
    }
    
    if (!selectedTemplate) return;
    setEditData({
      id: selectedTemplate.id,
      template_name: selectedTemplate.template_name || '',
      description: selectedTemplate.description || '',
      product_id: selectedTemplate.product_id || '',
      product_variant_id: selectedTemplate.product_variant_id || null,
      product_variant_name: selectedTemplate.product_variant_name || '',
      is_active: selectedTemplate.is_active || true,
      is_default: selectedTemplate.is_default || false
    });
    // Load existing consumption data
    loadTemplateConsumption(selectedTemplate.id);
    setValidationError('');
    setEditDialogOpen(true);
    handleCloseMenu();
  };

  const loadTemplateConsumption = async (templateId) => {
    try {
      const [capsResult, bottlesResult, labelsResult, suppliesResult] = await Promise.all([
        supabase.from('template_caps_consumption').select('*, cap:cap_id(*)').eq('template_id', templateId),
        supabase.from('template_bottles_consumption').select('*, bottle:bottle_id(*)').eq('template_id', templateId),
        supabase.from('template_labels_consumption').select('*, label:label_id(*)').eq('template_id', templateId),
        supabase.from('template_supplies_consumption').select('*, supply:supply_id(*)').eq('template_id', templateId)
      ]);

      setSelectedCaps(capsResult.data?.map(item => ({ ...item.cap, quantity_per_unit: item.quantity_per_unit })) || []);
      setSelectedBottles(bottlesResult.data?.map(item => ({ ...item.bottle, quantity_per_unit: item.quantity_per_unit })) || []);
      setSelectedLabels(labelsResult.data?.map(item => ({ ...item.label, quantity_per_unit: item.quantity_per_unit })) || []);
      setSelectedSupplies(suppliesResult.data?.map(item => ({ ...item.supply, quantity_per_unit: item.quantity_per_unit })) || []);
    } catch (error) {
      console.error('Error loading template consumption:', error);
    }
  };

  const handleSave = async () => {
    if (!hasPermission('production.line.templates.edit', 'write')) {
      setValidationError('You do not have permission to save templates.');
      return;
    }
    
    setSaving(true);
    setValidationError('');
    
    try {
      const requiredFields = [];
      
      if (!editData.template_name?.trim()) requiredFields.push('Template Name');
      if (!editData.product_id) requiredFields.push('Product');

      if (requiredFields.length > 0) {
        const errorMessage = `Please fill in the following required fields:\n• ${requiredFields.join('\n• ')}`;
        setValidationError(errorMessage);
        setSaving(false);
        return;
      }

      // Check if at least one component is selected
      const totalComponents = selectedCaps.length + selectedBottles.length + selectedLabels.length + selectedSupplies.length;
      if (totalComponents === 0) {
        setValidationError('At least one component must be configured.\n\nPlease add at least one cap, bottle, label, or supply to the template.');
        setSaving(false);
        return;
      }

      const templateData = {
        ...editData,
        company_id: companyId,
        updated_by: null,
        updated_at: new Date().toISOString()
      };

      if (!editData.id) {
        templateData.created_by = null;
        templateData.created_at = new Date().toISOString();
      }

      let result;
      if (editData.id) {
        const { data, error } = await supabase
          .from('production_line_templates')
          .update(templateData)
          .eq('id', editData.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('production_line_templates')
          .insert([templateData])
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      // Save consumption data
      await saveConsumptionData(result.id);

      setEditDialogOpen(false);
      await fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      setValidationError('Error saving template: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveConsumptionData = async (templateId) => {
    try {
      // Delete existing consumption data
      await Promise.all([
        supabase.from('template_caps_consumption').delete().eq('template_id', templateId),
        supabase.from('template_bottles_consumption').delete().eq('template_id', templateId),
        supabase.from('template_labels_consumption').delete().eq('template_id', templateId),
        supabase.from('template_supplies_consumption').delete().eq('template_id', templateId)
      ]);

      // Insert new consumption data
      const insertPromises = [];

      if (selectedCaps.length > 0) {
        const capsData = selectedCaps.map(cap => ({
          company_id: companyId,
          template_id: templateId,
          cap_id: cap.id,
          quantity_per_unit: cap.quantity_per_unit || 1,
          created_by: null
        }));
        insertPromises.push(supabase.from('template_caps_consumption').insert(capsData));
      }

      if (selectedBottles.length > 0) {
        const bottlesData = selectedBottles.map(bottle => ({
          company_id: companyId,
          template_id: templateId,
          bottle_id: bottle.id,
          quantity_per_unit: bottle.quantity_per_unit || 1,
          created_by: null
        }));
        insertPromises.push(supabase.from('template_bottles_consumption').insert(bottlesData));
      }

      if (selectedLabels.length > 0) {
        const labelsData = selectedLabels.map(label => ({
          company_id: companyId,
          template_id: templateId,
          label_id: label.id,
          quantity_per_unit: label.quantity_per_unit || 1,
          created_by: null
        }));
        insertPromises.push(supabase.from('template_labels_consumption').insert(labelsData));
      }

      if (selectedSupplies.length > 0) {
        const suppliesData = selectedSupplies.map(supply => ({
          company_id: companyId,
          template_id: templateId,
          supply_id: supply.id,
          quantity_per_unit: supply.quantity_per_unit || 1,
          created_by: null
        }));
        insertPromises.push(supabase.from('template_supplies_consumption').insert(suppliesData));
      }

      await Promise.all(insertPromises);
    } catch (error) {
      console.error('Error saving consumption data:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
    setSelectedCaps([]);
    setSelectedBottles([]);
    setSelectedLabels([]);
    setSelectedSupplies([]);
  };

  // Product selection dialog handlers
  const handleOpenProductSelection = () => {
    setProductSearchTerm('');
    setProductSelectionDialogOpen(true);
    setDialogSelectedProduct(null);
    setDialogSelectedVariant(null);
    setDialogProductVariants([]);
  };

  const handleCloseProductSelection = () => {
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
    setDialogSelectedVariant(null);
    setDialogProductVariants([]);
  };

  const handleDialogProductSelect = async (product) => {
    setDialogSelectedProduct(product);
    setDialogSelectedVariant(null);
    
    try {
      const { data: variants, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', product.id)
        .eq('is_active', true);

      if (error) throw error;
      const sorted = (variants || []).sort((a, b) => (parseFloat(b.volume_weight) || 0) - (parseFloat(a.volume_weight) || 0));
      setDialogProductVariants(sorted);
    } catch (error) {
      console.error('Error fetching product variants:', error);
      setDialogProductVariants([]);
    }
  };

  const handleDialogVariantSelect = (variant) => {
    setDialogSelectedVariant(variant);
  };

  const handleConfirmProductSelection = () => {
    if (dialogSelectedProduct) {
      setEditData({
        ...editData,
        product_id: dialogSelectedProduct.id,
        product_variant_id: dialogSelectedVariant?.id || null,
        product_variant_name: dialogSelectedVariant ? `${dialogSelectedVariant.volume_weight}${dialogSelectedVariant.volume_unit}` : ''
      });
      setProductSelectionDialogOpen(false);
    }
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim()) return products;
    
    const searchTerm = productSearchTerm.toLowerCase();
    return products.filter(product => 
      product.name?.toLowerCase().includes(searchTerm) ||
      product.description?.toLowerCase().includes(searchTerm) ||
      product.sku?.toLowerCase().includes(searchTerm)
    );
  };

  const addComponent = (component, type) => {
    const componentWithQuantity = { ...component, quantity_per_unit: 1 };
    
    switch (type) {
      case 'caps':
        if (!selectedCaps.find(c => c.id === component.id)) {
          setSelectedCaps([...selectedCaps, componentWithQuantity]);
        }
        break;
      case 'bottles':
        if (!selectedBottles.find(b => b.id === component.id)) {
          setSelectedBottles([...selectedBottles, componentWithQuantity]);
        }
        break;
      case 'labels':
        if (!selectedLabels.find(l => l.id === component.id)) {
          setSelectedLabels([...selectedLabels, componentWithQuantity]);
        }
        break;
      case 'supplies':
        if (!selectedSupplies.find(s => s.id === component.id)) {
          setSelectedSupplies([...selectedSupplies, componentWithQuantity]);
        }
        break;
    }
  };

  // Cap Selection Dialog Handlers
  const handleOpenCapSelectionDialog = () => {
    setCapSelectionDialogOpen(true);
    setCapSearchTerm('');
    setDialogSelectedCap(undefined);
  };

  const handleCloseCapSelectionDialog = () => {
    setCapSelectionDialogOpen(false);
    setCapSearchTerm('');
    setDialogSelectedCap(undefined);
  };

  const handleDialogCapSelect = (cap) => {
    setDialogSelectedCap(cap);
  };

  const handleConfirmCapSelection = () => {
    if (dialogSelectedCap) {
      addComponent(dialogSelectedCap, 'caps');
      setCapSelectionDialogOpen(false);
      setCapSearchTerm('');
      setDialogSelectedCap(undefined);
    }
  };

  const getFilteredCapsForSelection = () => {
    if (!capSearchTerm) return caps || [];
    
    const searchLower = capSearchTerm.toLowerCase();
    return (caps || []).filter(cap => 
      cap.name.toLowerCase().includes(searchLower) ||
      (cap.sku && cap.sku.toLowerCase().includes(searchLower)) ||
      (cap.material && cap.material.toLowerCase().includes(searchLower))
    );
  };

  // Bottle Selection Dialog Handlers
  const handleOpenBottleSelectionDialog = () => {
    setBottleSelectionDialogOpen(true);
    setBottleSearchTerm('');
    setDialogSelectedBottle(undefined);
  };

  const handleCloseBottleSelectionDialog = () => {
    setBottleSelectionDialogOpen(false);
    setBottleSearchTerm('');
    setDialogSelectedBottle(undefined);
  };

  const handleDialogBottleSelect = (bottle) => {
    setDialogSelectedBottle(bottle);
  };

  const handleConfirmBottleSelection = () => {
    if (dialogSelectedBottle) {
      addComponent(dialogSelectedBottle, 'bottles');
      setBottleSelectionDialogOpen(false);
      setBottleSearchTerm('');
      setDialogSelectedBottle(undefined);
    }
  };

  const getFilteredBottlesForSelection = () => {
    if (!bottleSearchTerm) return bottles || [];
    
    const searchLower = bottleSearchTerm.toLowerCase();
    return (bottles || []).filter(bottle => 
      bottle.name.toLowerCase().includes(searchLower) ||
      (bottle.sku && bottle.sku.toLowerCase().includes(searchLower)) ||
      (bottle.material && bottle.material.toLowerCase().includes(searchLower))
    );
  };

  // Label Selection Dialog Handlers
  const handleOpenLabelSelectionDialog = () => {
    setLabelSelectionDialogOpen(true);
    setLabelSearchTerm('');
    setDialogSelectedLabel(undefined);
  };

  const handleCloseLabelSelectionDialog = () => {
    setLabelSelectionDialogOpen(false);
    setLabelSearchTerm('');
    setDialogSelectedLabel(undefined);
  };

  const handleDialogLabelSelect = (label) => {
    setDialogSelectedLabel(label);
  };

  const handleConfirmLabelSelection = () => {
    if (dialogSelectedLabel) {
      addComponent(dialogSelectedLabel, 'labels');
      setLabelSelectionDialogOpen(false);
      setLabelSearchTerm('');
      setDialogSelectedLabel(undefined);
    }
  };

  const getFilteredLabelsForSelection = () => {
    if (!labelSearchTerm) return labels || [];
    
    const searchLower = labelSearchTerm.toLowerCase();
    return (labels || []).filter(label => 
      label.name.toLowerCase().includes(searchLower) ||
      (label.sku && label.sku.toLowerCase().includes(searchLower)) ||
      (label.material && label.material.toLowerCase().includes(searchLower))
    );
  };

  // Supply Selection Dialog Handlers
  const handleOpenSupplySelectionDialog = () => {
    setSupplySelectionDialogOpen(true);
    setSupplySearchTerm('');
    setDialogSelectedSupply(undefined);
  };

  const handleCloseSupplySelectionDialog = () => {
    setSupplySelectionDialogOpen(false);
    setSupplySearchTerm('');
    setDialogSelectedSupply(undefined);
  };

  const handleDialogSupplySelect = (supply) => {
    setDialogSelectedSupply(supply);
  };

  const handleConfirmSupplySelection = () => {
    if (dialogSelectedSupply) {
      addComponent(dialogSelectedSupply, 'supplies');
      setSupplySelectionDialogOpen(false);
      setSupplySearchTerm('');
      setDialogSelectedSupply(undefined);
    }
  };

  const getFilteredSuppliesForSelection = () => {
    if (!supplySearchTerm) return supplies || [];
    
    const searchLower = supplySearchTerm.toLowerCase();
    return (supplies || []).filter(supply => 
      supply.name.toLowerCase().includes(searchLower) ||
      (supply.sku && supply.sku.toLowerCase().includes(searchLower)) ||
      (supply.category && supply.category.toLowerCase().includes(searchLower))
    );
  };

  const handleTemplateRowClick = (template, event) => {
    // Prevent opening when clicking on action button
    if (event.target.closest('button') || event.target.closest('.MuiIconButton-root')) {
      return;
    }
    handleViewTemplate(template);
  };

  const handleViewTemplate = async (template) => {
    try {
      // Load full template details with consumption data and unit configuration
      const [capsResult, bottlesResult, labelsResult, suppliesResult, unitConfigResult] = await Promise.all([
        supabase.from('template_caps_consumption').select('*, cap:cap_id(*)').eq('template_id', template.id),
        supabase.from('template_bottles_consumption').select('*, bottle:bottle_id(*)').eq('template_id', template.id),
        supabase.from('template_labels_consumption').select('*, label:label_id(*)').eq('template_id', template.id),
        supabase.from('template_supplies_consumption').select('*, supply:supply_id(*)').eq('template_id', template.id),
        Promise.resolve({ data: null, error: null })
      ]);

      const templateWithConsumption = {
        ...template,
        caps_consumption: capsResult.data || [],
        bottles_consumption: bottlesResult.data || [],
        labels_consumption: labelsResult.data || [],
        supplies_consumption: suppliesResult.data || [],
      };

      setSelectedTemplateDetail(templateWithConsumption);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error loading template details:', error);
      setError('Failed to load template details');
    }
  };

  const removeComponent = (componentId, type) => {
    switch (type) {
      case 'caps':
        setSelectedCaps(selectedCaps.filter(c => c.id !== componentId));
        break;
      case 'bottles':
        setSelectedBottles(selectedBottles.filter(b => b.id !== componentId));
        break;
      case 'labels':
        setSelectedLabels(selectedLabels.filter(l => l.id !== componentId));
        break;
      case 'supplies':
        setSelectedSupplies(selectedSupplies.filter(s => s.id !== componentId));
        break;
    }
  };

  const updateComponentQuantity = (componentId, type, quantity) => {
    const newQuantity = parseInt(quantity, 10) || 1;
    
    switch (type) {
      case 'caps':
        setSelectedCaps(selectedCaps.map(c => 
          c.id === componentId ? { ...c, quantity_per_unit: newQuantity } : c
        ));
        break;
      case 'bottles':
        setSelectedBottles(selectedBottles.map(b => 
          b.id === componentId ? { ...b, quantity_per_unit: newQuantity } : b
        ));
        break;
      case 'labels':
        setSelectedLabels(selectedLabels.map(l => 
          l.id === componentId ? { ...l, quantity_per_unit: newQuantity } : l
        ));
        break;
      case 'supplies':
        setSelectedSupplies(selectedSupplies.map(s => 
          s.id === componentId ? { ...s, quantity_per_unit: newQuantity } : s
        ));
        break;
    }
  };

  // Permission check
  if (!hasPermission('production.line.templates.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PrecisionManufacturingIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Production Line Templates Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Production Line Templates. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <PrecisionManufacturingIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Production Templates
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Loading template and component data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 5,
            mt: 5,
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }} />
                <Typography variant="caption" sx={{ 
                  color: 'info.main', 
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.75rem'
                }}>
                  Template Management
                </Typography>
              </Box>
              
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                fontSize: '1.8rem',
                mb: 1,
                lineHeight: 1.2
              }}>
                Production Templates
              </Typography>
              
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '0.9rem',
                mb: 2.5,
                maxWidth: '400px'
              }}>
                Define what components (caps, bottles, labels, supplies) are consumed per product unit
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${filteredTemplates.length} ${filteredTemplates.length === 1 ? 'Template' : 'Templates'}`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }} 
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
              {hasPermission('production.line.templates.edit', 'write') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    bgcolor: 'primary.main',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                    }
                  }}
                >
                  New Template
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Search and Filter Bar */}
        <Box sx={{ mb: 8 }}>
          <Paper 
            sx={{ 
              p: 4, 
              mb: 5, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 500,
                color: 'primary.main',
                fontSize: '1.25rem'
              }}
            >
              Search & Filter Templates
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                placeholder="Search templates by name, product, or description..."
                variant="outlined"
                size="medium"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
              <Tooltip title="Refresh Data">
                <IconButton 
                  onClick={fetchTemplates}
                  sx={{ 
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    bgcolor: 'white',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        </Box>

        {/* Templates Content */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
        >
          <Box sx={{ bgcolor: 'transparent', p: 4 }}>
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            }}>
              <TableContainer sx={{ overflow: 'visible' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>Template Name</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>Product</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>Variant</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}>Status</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3,
                        width: 120
                      }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                          <Stack spacing={2} alignItems="center">
                            <PrecisionManufacturingIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {searchTerm ? 'No templates found matching your search' : 'No production templates yet'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm ? 'Try adjusting your search terms' : 'Create your first production template to get started'}
                            </Typography>
                            {!searchTerm && hasPermission('production.line.templates.edit', 'write') && (
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCreateNew}
                                sx={{ 
                                  mt: 2,
                                  borderRadius: 3,
                                  px: 4,
                                  py: 1.5,
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  textTransform: 'none',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': {
                                    transform: 'translateY(-1px) scale(1.02)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                                  }
                                }}
                              >
                                Create First Template
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((template) => (
                        <TableRow 
                          key={template.id} 
                          hover 
                          onClick={(e) => handleTemplateRowClick(template, e)}
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2.5,
                              px: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                              {template.template_name}
                            </Typography>
                            {template.description && (
                              <Typography variant="caption" color="text.secondary">
                                {template.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                              {template.product?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                              {template.product_variant_name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={template.is_active ? 'Active' : 'Inactive'}
                              color={template.is_active ? 'success' : 'default'}
                              size="small"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: 2,
                                fontSize: '0.75rem'
                              }}
                            />
                            {template.is_default && (
                              <Chip 
                                label="Default" 
                                color="primary" 
                                size="small" 
                                sx={{ ml: 1, fontWeight: 500, fontSize: '0.75rem' }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMenu(e, template);
                              }}
                              disabled={!hasPermission('production.line.templates.edit', 'write') && !hasPermission('production.line.templates.delete', 'delete')}
                              sx={{ 
                                p: 1,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                bgcolor: (!hasPermission('production.line.templates.edit', 'write') && !hasPermission('production.line.templates.delete', 'delete')) ? 'grey.300' : 'black',
                                color: (!hasPermission('production.line.templates.edit', 'write') && !hasPermission('production.line.templates.delete', 'delete')) ? 'grey.500' : 'white',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': (!hasPermission('production.line.templates.edit', 'write') && !hasPermission('production.line.templates.delete', 'delete')) ? {} : { 
                                  bgcolor: 'grey.900',
                                  color: 'white',
                                  transform: 'translateY(-2px) scale(1.05)',
                                  boxShadow: `0 6px 18px ${alpha(theme.palette.grey[900], 0.4)}`,
                                  border: `1px solid ${theme.palette.grey[900]}`
                                },
                                '&.Mui-disabled': {
                                  bgcolor: 'grey.300',
                                  color: 'grey.500',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                }
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!loading && (
                <Box sx={{ 
                  borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                  borderRadius: '0 0 12px 12px'
                }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredTemplates.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ 
                      px: 3,
                      py: 2,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary
                      }
                    }}
                  />
                </Box>
              )}
            </Paper>
          </Box>
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {hasPermission('production.line.templates.edit', 'write') && (
            <MenuItem onClick={handleEditTemplate}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Template</ListItemText>
            </MenuItem>
          )}
          {hasPermission('production.line.templates.delete', 'delete') && (
            <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Template</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Delete Production Template</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the template "{selectedTemplate?.template_name}"? 
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button 
              onClick={handleDeleteTemplate} 
              color="error" 
              variant="contained"
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create/Edit Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={handleCancel} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: 'none',
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              maxHeight: '95vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 4, 
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            color: theme.palette.text.primary,
            fontWeight: 500
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'white',
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PrecisionManufacturingIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 0.5 }}>
                  {editData.id ? 'Edit Production Template' : 'Create New Production Template'}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                  Define component consumption for line production
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          
          {validationError && (
            <Box sx={{ px: 3, pt: 3 }}>
              <Alert 
                severity="error" 
                sx={{ 
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  fontWeight: 500,
                  '& .MuiAlert-message': {
                    whiteSpace: 'pre-line'
                  }
                }}
              >
                {validationError}
              </Alert>
            </Box>
          )}
          
          <DialogContent sx={{ backgroundColor: '#f8fafc', p: 3, mt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Basic Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={editData.template_name}
                  onChange={(e) => setEditData({ ...editData, template_name: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)'
                    }
                  }}
                />
              </Grid>

              {/* Product Selection */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Product Selection
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenProductSelection}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    justifyContent: 'flex-start',
                    px: 3,
                    py: 2.5,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <InventoryIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      {editData.product_id ? (
                        <>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {products.find(p => p.id === editData.product_id)?.name || 'Unknown Product'}
                          </Typography>
                          {editData.product_variant_name && (
                            <Typography variant="caption" color="text.secondary">
                              Variant: {editData.product_variant_name}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                          Select Product & Variant
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Button>
              </Grid>

              {/* Component Selection */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Component Consumption
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 3 }}>
                  Define what components are consumed per product unit
                </Typography>
              </Grid>

              {/* Caps Selection */}
              <Grid item xs={12}>
                <Accordion sx={{ borderRadius: 2, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Caps ({selectedCaps.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenCapSelectionDialog}
                          startIcon={<AddIcon />}
                          sx={{
                            height: 40,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: 'white',
                            justifyContent: 'flex-start',
                            px: 3,
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            Select Cap
                          </Typography>
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {selectedCaps.map((cap) => (
                          <Box key={cap.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography sx={{ flex: 1 }}>{cap.name}</Typography>
                            <TextField
                              size="small"
                              label="Qty per unit"
                              type="number"
                              value={cap.quantity_per_unit}
                              onChange={(e) => updateComponentQuantity(cap.id, 'caps', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 1, step: 1 }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => removeComponent(cap.id, 'caps')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Bottles Selection */}
              <Grid item xs={12}>
                <Accordion sx={{ borderRadius: 2, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Bottles ({selectedBottles.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenBottleSelectionDialog}
                          startIcon={<AddIcon />}
                          sx={{
                            height: 40,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: 'white',
                            justifyContent: 'flex-start',
                            px: 3,
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            Select Bottle
                          </Typography>
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {selectedBottles.map((bottle) => (
                          <Box key={bottle.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography sx={{ flex: 1 }}>{bottle.name}</Typography>
                            <TextField
                              size="small"
                              label="Qty per unit"
                              type="number"
                              value={bottle.quantity_per_unit}
                              onChange={(e) => updateComponentQuantity(bottle.id, 'bottles', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 1, step: 1 }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => removeComponent(bottle.id, 'bottles')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Labels Selection */}
              <Grid item xs={12}>
                <Accordion sx={{ borderRadius: 2, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Labels ({selectedLabels.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenLabelSelectionDialog}
                          startIcon={<AddIcon />}
                          sx={{
                            height: 40,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: 'white',
                            justifyContent: 'flex-start',
                            px: 3,
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            Select Label
                          </Typography>
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {selectedLabels.map((label) => (
                          <Box key={label.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography sx={{ flex: 1 }}>{label.name}</Typography>
                            <TextField
                              size="small"
                              label="Qty per unit"
                              type="number"
                              value={label.quantity_per_unit}
                              onChange={(e) => updateComponentQuantity(label.id, 'labels', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 1, step: 1 }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => removeComponent(label.id, 'labels')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Supplies Selection */}
              <Grid item xs={12}>
                <Accordion sx={{ borderRadius: 2, mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Supplies ({selectedSupplies.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenSupplySelectionDialog}
                          startIcon={<AddIcon />}
                          sx={{
                            height: 40,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            background: 'white',
                            justifyContent: 'flex-start',
                            px: 3,
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            Select Supply
                          </Typography>
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {selectedSupplies.map((supply) => (
                          <Box key={supply.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography sx={{ flex: 1 }}>{supply.name}</Typography>
                            <TextField
                              size="small"
                              label="Qty per unit"
                              type="number"
                              value={supply.quantity_per_unit}
                              onChange={(e) => updateComponentQuantity(supply.id, 'supplies', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 1, step: 1 }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => removeComponent(supply.id, 'supplies')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 4, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
          }}>
            <Button 
              onClick={handleCancel} 
              startIcon={<CancelIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Selection Dialog */}
        <Dialog
          open={productSelectionDialogOpen}
          onClose={handleCloseProductSelection}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 500,
            fontSize: '1.2rem',
            color: 'primary.main',
            borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InventoryIcon sx={{ mr: 2 }} />
              Select Product & Variant
            </Box>
            <IconButton 
              onClick={handleCloseProductSelection} 
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search products by name, description, or SKU..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InventoryIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            
            {!dialogSelectedProduct ? (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 500, width: 100 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredProducts().map((product) => (
                      <TableRow
                        key={product.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDialogProductSelect(product)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {product.name}
                          </Typography>
                          {product.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {product.sku}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDialogProductSelect(product);
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Selected Product:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {dialogSelectedProduct.name}
                  </Typography>
                  <Button size="small" onClick={() => setDialogSelectedProduct(null)} sx={{ mt: 1 }}>
                    Change Product
                  </Button>
                </Box>

                {dialogProductVariants.length > 0 ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                      Select Variant:
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 500 }}>Variant</TableCell>
                            <TableCell sx={{ fontWeight: 500, width: 100 }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dialogProductVariants.map((variant) => (
                            <TableRow key={variant.id} hover sx={{ cursor: 'pointer' }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {variant.volume_weight}{variant.volume_unit}
                                </Typography>
                                {variant.sku && (
                                  <Typography variant="caption" color="text.secondary">
                                    SKU: {variant.sku}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant={dialogSelectedVariant?.id === variant.id ? "contained" : "outlined"}
                                  onClick={() => handleDialogVariantSelect(variant)}
                                >
                                  {dialogSelectedVariant?.id === variant.id ? "Selected" : "Select"}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      ✓ This product has no variants. Ready to proceed.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseProductSelection}>Cancel</Button>
            <Button
              onClick={handleConfirmProductSelection}
              variant="contained"
              disabled={!dialogSelectedProduct || (dialogProductVariants.length > 0 && !dialogSelectedVariant)}
            >
              Confirm Selection
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cap Selection Dialog */}
        <Dialog
          open={capSelectionDialogOpen}
          onClose={handleCloseCapSelectionDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Cap
            </Typography>
            <IconButton onClick={handleCloseCapSelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search caps..."
                value={capSearchTerm}
                onChange={(e) => setCapSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Cap</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredCapsForSelection().map((cap) => (
                    <TableRow 
                      key={cap.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {cap.name}
                          </Typography>
                          {cap.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {cap.sku}
                            </Typography>
                          )}
                          {cap.material && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Material: {cap.material}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDialogCapSelect(cap)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedCap?.id === cap.id ? 'black' : 'white',
                            color: dialogSelectedCap?.id === cap.id ? 'white' : 'text.primary',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                              bgcolor: dialogSelectedCap?.id === cap.id ? 'black' : 'white',
                              color: dialogSelectedCap?.id === cap.id ? 'white' : 'text.primary'
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredCapsForSelection().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {capSearchTerm ? 'No caps found matching your search.' : 'No caps available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={() => {
                localStorage.setItem('inventory_type_preference', 'caps');
                window.open(`/company/${companyId}/inventory/inventory/inventory-configurations`, '_blank');
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
                color: 'success.main',
                '&:hover': {
                  border: `1px solid ${theme.palette.success.main}`,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Cap
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseCapSelectionDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmCapSelection}
                variant="contained"
                disabled={!dialogSelectedCap}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Bottle Selection Dialog */}
        <Dialog
          open={bottleSelectionDialogOpen}
          onClose={handleCloseBottleSelectionDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Bottle
            </Typography>
            <IconButton onClick={handleCloseBottleSelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search bottles..."
                value={bottleSearchTerm}
                onChange={(e) => setBottleSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Bottle</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredBottlesForSelection().map((bottle) => (
                    <TableRow 
                      key={bottle.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {bottle.name}
                          </Typography>
                          {bottle.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {bottle.sku}
                            </Typography>
                          )}
                          {bottle.material && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Material: {bottle.material}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDialogBottleSelect(bottle)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedBottle?.id === bottle.id ? 'black' : 'white',
                            color: dialogSelectedBottle?.id === bottle.id ? 'white' : 'text.primary',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                              bgcolor: dialogSelectedBottle?.id === bottle.id ? 'black' : 'white',
                              color: dialogSelectedBottle?.id === bottle.id ? 'white' : 'text.primary'
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredBottlesForSelection().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {bottleSearchTerm ? 'No bottles found matching your search.' : 'No bottles available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={() => {
                localStorage.setItem('inventory_type_preference', 'bottles');
                window.open(`/company/${companyId}/inventory/inventory/inventory-configurations`, '_blank');
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
                color: 'success.main',
                '&:hover': {
                  border: `1px solid ${theme.palette.success.main}`,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Bottle
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseBottleSelectionDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmBottleSelection}
                variant="contained"
                disabled={!dialogSelectedBottle}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Label Selection Dialog */}
        <Dialog
          open={labelSelectionDialogOpen}
          onClose={handleCloseLabelSelectionDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Label
            </Typography>
            <IconButton onClick={handleCloseLabelSelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search labels..."
                value={labelSearchTerm}
                onChange={(e) => setLabelSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Label</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredLabelsForSelection().map((label) => (
                    <TableRow 
                      key={label.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {label.name}
                          </Typography>
                          {label.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {label.sku}
                            </Typography>
                          )}
                          {label.material && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Material: {label.material}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDialogLabelSelect(label)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedLabel?.id === label.id ? 'black' : 'white',
                            color: dialogSelectedLabel?.id === label.id ? 'white' : 'text.primary',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                              bgcolor: dialogSelectedLabel?.id === label.id ? 'black' : 'white',
                              color: dialogSelectedLabel?.id === label.id ? 'white' : 'text.primary'
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredLabelsForSelection().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {labelSearchTerm ? 'No labels found matching your search.' : 'No labels available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={() => {
                localStorage.setItem('inventory_type_preference', 'labels');
                window.open(`/company/${companyId}/inventory/inventory/inventory-configurations`, '_blank');
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
                color: 'success.main',
                '&:hover': {
                  border: `1px solid ${theme.palette.success.main}`,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Label
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseLabelSelectionDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLabelSelection}
                variant="contained"
                disabled={!dialogSelectedLabel}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Supply Selection Dialog */}
        <Dialog
          open={supplySelectionDialogOpen}
          onClose={handleCloseSupplySelectionDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Supply
            </Typography>
            <IconButton onClick={handleCloseSupplySelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search supplies..."
                value={supplySearchTerm}
                onChange={(e) => setSupplySearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Supply</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredSuppliesForSelection().map((supply) => (
                    <TableRow 
                      key={supply.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {supply.name}
                          </Typography>
                          {supply.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {supply.sku}
                            </Typography>
                          )}
                          {supply.category && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Category: {supply.category}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDialogSupplySelect(supply)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedSupply?.id === supply.id ? 'black' : 'white',
                            color: dialogSelectedSupply?.id === supply.id ? 'white' : 'text.primary',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                              bgcolor: dialogSelectedSupply?.id === supply.id ? 'black' : 'white',
                              color: dialogSelectedSupply?.id === supply.id ? 'white' : 'text.primary'
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredSuppliesForSelection().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {supplySearchTerm ? 'No supplies found matching your search.' : 'No supplies available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={() => {
                localStorage.setItem('inventory_type_preference', 'supplies');
                window.open(`/company/${companyId}/inventory/inventory/inventory-configurations`, '_blank');
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
                color: 'success.main',
                '&:hover': {
                  border: `1px solid ${theme.palette.success.main}`,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Supply
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseSupplySelectionDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSupplySelection}
                variant="contained"
                disabled={!dialogSelectedSupply}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Template Details Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedTemplateDetail(null);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              maxHeight: '90vh',
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              bgcolor: alpha(theme.palette.grey[50], 0.95),
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          {selectedTemplateDetail && (
            <>
              <DialogTitle sx={{ 
                pb: 2, 
                pt: 3,
                px: 3,
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'white'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }}>
                    <PrecisionManufacturingIcon sx={{ color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
                      Template Details
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
                      {selectedTemplateDetail.template_name} - {selectedTemplateDetail.product?.name}
                    </Typography>
                  </Box>
                </Box>
                <IconButton 
                  onClick={() => setDetailDialogOpen(false)} 
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.grey[400], 0.1),
                      color: 'text.primary'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 4 }}>
                  <Grid container spacing={4}>
                    {/* Basic Template Info */}
                    <Grid item xs={12}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        mb: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Template Information
                          </Typography>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Template Name
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {selectedTemplateDetail.template_name}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Product
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {selectedTemplateDetail.product?.name || 'N/A'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Variant
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {selectedTemplateDetail.product_variant_name || 'N/A'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                  Status
                                </Typography>
                                <Chip 
                                  label={selectedTemplateDetail.is_active ? 'Active' : 'Inactive'}
                                  color={selectedTemplateDetail.is_active ? 'success' : 'default'}
                                  size="medium"
                                  sx={{ 
                                    fontWeight: 500,
                                    border: `1px solid`,
                                    borderColor: selectedTemplateDetail.is_active ? theme.palette.success.main : theme.palette.grey[400]
                                  }}
                                />
                                {selectedTemplateDetail.is_default && (
                                  <Chip 
                                    label="Default" 
                                    color="primary" 
                                    size="medium" 
                                    sx={{ ml: 1, fontWeight: 500 }}
                                  />
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                          {selectedTemplateDetail.description && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Description
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedTemplateDetail.description}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Component Consumption */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: '100%',
                        minHeight: '300px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Caps, Bottles & Labels Consumption
                          </Typography>
                          
                          {/* Caps */}
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                              Caps ({selectedTemplateDetail.caps_consumption?.length || 0})
                            </Typography>
                            {selectedTemplateDetail.caps_consumption?.length > 0 ? (
                              selectedTemplateDetail.caps_consumption.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {item.cap?.name || 'Unknown Cap'}
                                    </Typography>
                                    {item.cap?.sku && (
                                      <Typography variant="caption" color="text.secondary">
                                        SKU: {item.cap.sku}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip 
                                    label={`${item.quantity_per_unit} per unit`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha(theme.palette.info.main, 0.1),
                                      color: 'info.main',
                                      fontWeight: 500
                                    }}
                                  />
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No caps configured
                              </Typography>
                            )}
                          </Box>

                          {/* Bottles */}
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                              Bottles ({selectedTemplateDetail.bottles_consumption?.length || 0})
                            </Typography>
                            {selectedTemplateDetail.bottles_consumption?.length > 0 ? (
                              selectedTemplateDetail.bottles_consumption.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {item.bottle?.name || 'Unknown Bottle'}
                                    </Typography>
                                    {item.bottle?.sku && (
                                      <Typography variant="caption" color="text.secondary">
                                        SKU: {item.bottle.sku}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip 
                                    label={`${item.quantity_per_unit} per unit`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                                      color: 'warning.main',
                                      fontWeight: 500
                                    }}
                                  />
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No bottles configured
                              </Typography>
                            )}
                          </Box>

                          {/* Labels */}
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                              Labels ({selectedTemplateDetail.labels_consumption?.length || 0})
                            </Typography>
                            {selectedTemplateDetail.labels_consumption?.length > 0 ? (
                              selectedTemplateDetail.labels_consumption.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {item.label?.name || 'Unknown Label'}
                                    </Typography>
                                    {item.label?.sku && (
                                      <Typography variant="caption" color="text.secondary">
                                        SKU: {item.label.sku}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip 
                                    label={`${item.quantity_per_unit} per unit`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha(theme.palette.success.main, 0.1),
                                      color: 'success.main',
                                      fontWeight: 500
                                    }}
                                  />
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No labels configured
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Supplies */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: '100%',
                        minHeight: '300px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Supplies Consumption
                          </Typography>

                          {/* Supplies */}
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                              Supplies ({selectedTemplateDetail.supplies_consumption?.length || 0})
                            </Typography>
                            {selectedTemplateDetail.supplies_consumption?.length > 0 ? (
                              selectedTemplateDetail.supplies_consumption.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {item.supply?.name || 'Unknown Supply'}
                                    </Typography>
                                    {item.supply?.sku && (
                                      <Typography variant="caption" color="text.secondary">
                                        SKU: {item.supply.sku}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip 
                                    label={`${item.quantity_per_unit} per unit`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha(theme.palette.grey[800], 0.1),
                                      color: 'grey.800',
                                      fontWeight: 500,
                                      border: `1px solid ${alpha(theme.palette.grey[800], 0.3)}`
                                    }}
                                  />
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No supplies configured
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                  </Grid>
                </Box>
              </DialogContent>
              
              <DialogActions sx={{ 
                p: 3, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: 'white'
              }}>
                <Button 
                  onClick={() => setDetailDialogOpen(false)}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  Close
                </Button>
                {hasPermission('production.line.templates.edit', 'write') && (
                  <Button 
                    onClick={() => {
                      setSelectedTemplate(selectedTemplateDetail);
                      handleEditTemplate();
                      setDetailDialogOpen(false);
                    }}
                    variant="contained"
                    startIcon={<EditIcon />}
                    sx={{ 
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    Edit Template
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

      </Container>
    </Fade>
  );
};

export default ProductionLineTemplates;